/**
 * 2.1 §1.2 — página «Club nuvvu» del admin: socios (estado, plan, origen), altas y bajas del mes,
 * puntos dados y gastados, coste del mes (partes fijas perdonadas + puntos), garantía de ahorro y
 * sorteos entre socios (admin-clubRaffle). Los parámetros se editan en Ajustes › Club nuvvu.
 */
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, orderBy, query, where } from 'firebase/firestore';
import { api } from '@nuvvu/shared/api';
import { clubLive, clubMonthKey } from '@nuvvu/shared';
import { call, errorText } from '../api.js';
import { db } from '../firebase.js';
import { btn, card, field, money, muted, Notice, str, Table, td, when } from '../lib/ui.js';

type Doc = Record<string, unknown>;

const ms = (v: unknown): number =>
  v && typeof v === 'object' && 'toMillis' in v ? (v as { toMillis: () => number }).toMillis() : 0;
const eur = (cents: number): string =>
  (cents / 100).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
const amt = (v: unknown): { amount: number; currency: string } | null =>
  v && typeof v === 'object' && typeof (v as Doc).amount === 'number'
    ? { amount: (v as Doc).amount as number, currency: str((v as Doc).currency) }
    : null;

/** Suma importes por moneda: «12,00 € · 3.400 Ft». */
function byCurrency(items: { amount: number; currency: string }[]): string {
  const m = new Map<string, number>();
  for (const i of items) m.set(i.currency, (m.get(i.currency) ?? 0) + i.amount);
  if (m.size === 0) return '—';
  return [...m.entries()].map(([c, a]) => money({ amount: a, currency: c })).join(' · ');
}

function Stat(props: { label: string; value: string; hint?: string }): ReactElement {
  return (
    <div style={{ ...card, gap: 2 }}>
      <span style={muted}>{props.label}</span>
      <strong style={{ fontSize: 20 }}>{props.value}</strong>
      {props.hint && <span style={{ ...muted, fontSize: 12 }}>{props.hint}</span>}
    </div>
  );
}

function Raffle(props: { onDone: () => void }): ReactElement {
  const [title, setTitle] = useState('');
  const [winners, setWinners] = useState('1');
  const [prize, setPrize] = useState('');
  const [days, setDays] = useState('90');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Una clave por sorteo: un doble clic o un reintento no sortea (ni paga) dos veces.
  const [key, setKey] = useState(() => `raffle-${crypto.randomUUID()}`);
  const run = (dry: boolean): void => {
    setMsg(null);
    setErr(null);
    const prizeCents = Math.round(Number(prize.replace(',', '.') || '0') * 100);
    if (!dry && !title.trim()) {
      setErr('Pon un título al sorteo.');
      return;
    }
    if (
      !dry &&
      !window.confirm(
        `¿Sortear ${winners} ganador(es) entre los socios${prizeCents > 0 ? ` con ${eur(prizeCents)} de saldo cada uno` : ''}? No se puede deshacer.`,
      )
    )
      return;
    setBusy(true);
    call(api.admin.clubRaffle, {
      title: title.trim() || 'Sorteo',
      winners: Math.max(1, Math.round(Number(winners) || 1)),
      ...(prizeCents > 0 ? { prize: { amount: prizeCents, currency: 'EUR' } } : {}),
      prize_valid_days: Math.max(0, Math.round(Number(days) || 0)),
      ...(dry ? { dry_run: true } : { key }),
    })
      .then((r) => {
        if (dry) setMsg(`Entrarían ${String(r.eligible)} socios.`);
        else {
          setMsg(
            `Ganadores (${String(r.winners.length)} de ${String(r.eligible)}): ${r.winners
              .map((w) => (w.username ? `@${w.username}` : w.uid))
              .join(', ')}. Ya están avisados${prizeCents > 0 ? ' y tienen el saldo' : ''}.`,
          );
          setKey(`raffle-${crypto.randomUUID()}`);
          props.onDone();
        }
      })
      .catch((e: unknown) => {
        setErr(errorText(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };
  return (
    <section style={{ ...card, maxWidth: 560 }}>
      <strong>Sorteo entre socios</strong>
      <span style={muted}>
        Entran los socios con la cuota al día (también en el mes gratis; no los que tienen un cobro
        pendiente). Elige al azar, avisa a los ganadores y, si pones premio, se lo da como saldo de
        premio (no se retira). Si el premio es otra cosa (una tarjeta regalo…), déjalo vacío y
        entrégalo a mano.
      </span>
      <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
        Título (lo ven los ganadores)
        <input
          style={field}
          value={title}
          placeholder="Sorteo de diciembre del Club"
          onChange={(e) => {
            setTitle(e.target.value);
          }}
        />
      </label>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
        <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
          Ganadores
          <input
            style={field}
            inputMode="numeric"
            value={winners}
            onChange={(e) => {
              setWinners(e.target.value);
            }}
          />
        </label>
        <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
          Premio por ganador (€, opcional)
          <input
            style={field}
            inputMode="decimal"
            value={prize}
            onChange={(e) => {
              setPrize(e.target.value);
            }}
          />
        </label>
        <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
          Días que dura (0 = siempre)
          <input
            style={field}
            inputMode="numeric"
            value={days}
            onChange={(e) => {
              setDays(e.target.value);
            }}
          />
        </label>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          style={btn(false)}
          disabled={busy}
          onClick={() => {
            run(true);
          }}
        >
          Contar socios
        </button>
        <button
          type="button"
          style={btn(true)}
          disabled={busy}
          onClick={() => {
            run(false);
          }}
        >
          Sortear
        </button>
      </div>
      <Notice text={msg} />
      <Notice text={err} error />
    </section>
  );
}

export function ClubAdminPage(): ReactElement {
  const [members, setMembers] = useState<Doc[] | null>(null);
  const [lots, setLots] = useState<Doc[]>([]);
  const [raffles, setRaffles] = useState<Doc[]>([]);
  const [fixedFee, setFixedFee] = useState(70);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'live' | 'all'>('live');
  const load = (): void => {
    Promise.all([
      getDocs(collection(db, 'clubMemberships')),
      getDocs(query(collection(db, 'userPerks'), where('source', '==', 'club'))),
      getDocs(query(collection(db, 'clubRaffles'), orderBy('created_at', 'desc'))),
      getDoc(doc(db, 'settings', 'fees')),
    ])
      .then(([m, p, r, f]) => {
        setMembers(m.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLots(p.docs.map((d) => ({ id: d.id, ...d.data() })));
        setRaffles(r.docs.map((d) => ({ id: d.id, ...d.data() })));
        const fixed = amt(f.data()?.protection_fee_fixed);
        if (fixed?.currency === 'EUR') setFixedFee(fixed.amount);
      })
      .catch((e: unknown) => {
        setError(errorText(e));
        setMembers([]);
      });
  };
  useEffect(load, []);

  const s = useMemo(() => {
    const list = members ?? [];
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    const month = clubMonthKey();
    const live = list.filter((m) => clubLive(str(m.status)));
    const count = (pred: (m: Doc) => boolean): number => live.filter(pred).length;
    const waived = list.reduce(
      (n, m) => n + ((m.fixed_waived as Record<string, number> | undefined)?.[month] ?? 0),
      0,
    );
    const pointsLots = lots.filter((l) => str(l.reason).startsWith('order:'));
    const pointsMonth = pointsLots.filter((l) => ms(l.created_at) >= monthStart);
    const savings = list.map((m) => (m.savings ?? {}) as { saved_eur?: number; paid_eur?: number });
    return {
      live: live.length,
      trial: count((m) => m.status === 'trialing'),
      pastDue: count((m) => m.status === 'past_due'),
      annual: count((m) => m.plan === 'annual'),
      stripe: count((m) => m.source === 'stripe'),
      play: count((m) => m.source === 'play_store'),
      apple: count((m) => m.source === 'app_store'),
      joinedMonth: list.filter((m) => ms(m.first_joined_at) >= monthStart).length,
      leftMonth: list.filter((m) => !clubLive(str(m.status)) && ms(m.updated_at) >= monthStart)
        .length,
      waived,
      issued: byCurrency(lots.map((l) => amt(l.amount)).filter((x) => x !== null)),
      spent: byCurrency(
        lots
          .map((l) => {
            const a = amt(l.amount);
            const r = amt(l.remaining) ?? a;
            return a && r ? { amount: a.amount - r.amount, currency: a.currency } : null;
          })
          .filter((x) => x !== null),
      ),
      pointsMonth: byCurrency(pointsMonth.map((l) => amt(l.amount)).filter((x) => x !== null)),
      paid: savings.reduce((n, x) => n + (x.paid_eur ?? 0), 0),
      saved: savings.reduce((n, x) => n + (x.saved_eur ?? 0), 0),
      guaranteeDone: list.filter((m) => m.guarantee_done === true).length,
      guaranteeRefund: list.reduce(
        (n, m) => n + (typeof m.guarantee_refund_eur === 'number' ? m.guarantee_refund_eur : 0),
        0,
      ),
    };
  }, [members, lots]);

  const rows = (members ?? [])
    .filter((m) => filter === 'all' || clubLive(str(m.status)))
    .sort((a, b) => ms(b.updated_at) - ms(a.updated_at));

  return (
    <>
      <h1 style={{ margin: 0 }}>Club nuvvu</h1>
      <p style={muted}>
        Socios, puntos y coste. Precios y ventajas en{' '}
        <Link to="/ajustes/club">Ajustes › Club nuvvu</Link>; el interruptor está en Sistema.
      </p>
      <Notice text={error} error />
      {!members && <p style={muted}>Cargando…</p>}
      {members && (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
              gap: 10,
            }}
          >
            <Stat
              label="Socios activos"
              value={String(s.live)}
              hint={`${String(s.trial)} en mes gratis · ${String(s.pastDue)} con cobro pendiente`}
            />
            <Stat
              label="Plan / origen"
              value={`${String(s.annual)} anuales`}
              hint={`web ${String(s.stripe)} · Google Play ${String(s.play)} · App Store ${String(s.apple)}`}
            />
            <Stat
              label="Este mes"
              value={`+${String(s.joinedMonth)} / −${String(s.leftMonth)}`}
              hint="altas nuevas / bajas"
            />
            <Stat label="Puntos dados (total)" value={s.issued} hint={`gastados: ${s.spent}`} />
            <Stat
              label="Coste del mes"
              value={eur(s.waived * fixedFee)}
              hint={`${String(s.waived)} compras sin parte fija · puntos del mes: ${s.pointsMonth}`}
            />
            <Stat
              label="Garantía de ahorro"
              value={`${eur(s.saved)} ahorrado`}
              hint={`pagado ${eur(s.paid)} · liquidadas ${String(s.guaranteeDone)} (${eur(s.guaranteeRefund)} devuelto)`}
            />
          </div>
          <Raffle onDone={load} />
          {raffles.length > 0 && (
            <Table head={['Sorteo', 'Fecha', 'Socios', 'Ganadores', 'Premio']}>
              {raffles.map((r) => (
                <tr key={str(r.id)}>
                  <td style={td}>{str(r.title)}</td>
                  <td style={td}>{when(r.created_at)}</td>
                  <td style={td}>{str(r.eligible)}</td>
                  <td style={td}>
                    {((r.winners as string[] | undefined) ?? []).map((u) => (
                      <Link key={u} to={`/usuarios/${u}`} style={{ marginRight: 6 }}>
                        {u.slice(0, 8)}
                      </Link>
                    ))}
                  </td>
                  <td style={td}>{r.prize ? money(r.prize) : '—'}</td>
                </tr>
              ))}
            </Table>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              style={btn(filter === 'live')}
              onClick={() => {
                setFilter('live');
              }}
            >
              Activos
            </button>
            <button
              type="button"
              style={btn(filter === 'all')}
              onClick={() => {
                setFilter('all');
              }}
            >
              Todos
            </button>
          </div>
          <Table
            head={[
              'Usuario',
              'Estado',
              'Plan',
              'Origen',
              'Renueva',
              'Baja el',
              'Alta',
              'Ahorrado',
              'Pagado',
            ]}
          >
            {rows.map((m) => {
              const sv = (m.savings ?? {}) as { saved_eur?: number; paid_eur?: number };
              return (
                <tr key={str(m.id)}>
                  <td style={td}>
                    <Link to={`/usuarios/${str(m.id)}`}>{str(m.id).slice(0, 10)}</Link>
                  </td>
                  <td style={td}>{str(m.status)}</td>
                  <td style={td}>{m.plan === 'annual' ? 'anual' : 'mensual'}</td>
                  <td style={td}>{str(m.source)}</td>
                  <td style={td}>{when(m.renews_at)}</td>
                  <td style={td}>{when(m.cancel_at)}</td>
                  <td style={td}>{when(m.first_joined_at)}</td>
                  <td style={td}>{eur(sv.saved_eur ?? 0)}</td>
                  <td style={td}>{eur(sv.paid_eur ?? 0)}</td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td style={td} colSpan={9}>
                  Ningún socio.
                </td>
              </tr>
            )}
          </Table>
        </>
      )}
    </>
  );
}
